<template>
  <v-menu
      ref="menu"
      v-model="menu"
      :return-value.sync="localDate"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-x
      min-width="290px"
  >
    <template v-slot:activator="{ on }">
      <v-text-field
          :disabled="disabled"
          :rules="getRules"
          :value="computedDateFormatted"
          :label="label"
          :hint="hint"
          :clearable="isClearable"
          :readonly="readonly"
          persistent-hint
          outlined
          v-model="maskedDate"
          v-mask="disableMask ? null : mask"
          placeholder="dd.mm.yyyy"
          append-icon="date_range"
          @click:append="on.click"
          @click:clear="onClearClicked"
          @input="changedMaskDate"
          :tabindex="tabindex"
          :loading="isLoading"
      />
    </template>
    <v-date-picker
        :disabled="disabled"
        v-model="localDate"
        scrollable
        :readonly="readonly"
        @input="changeDate"
        :min="onlyFuture ? today : minDate ? minDate : undefined"
        :max="onlyPast ? yesterday : maxDate ? maxDate : undefined"
    >
    </v-date-picker>
  </v-menu>
</template>

<script>
import {ruleRequired, ruleValidDate, rulePastDate, ruleFutureDate, ruleMaxDate, ruleMinDate} from "@/modules/core/services/rules";
import DateUtils from "@/modules/core/services/date-utils";

export default {
  name: "DateField",
  props: {
    label: String,
    hint: String,
    isClearable: Boolean,
    isRequired: Boolean,
    onlyPast: Boolean,
    onlyFuture: Boolean,
    disabled: Boolean,
    initDate: String,
    tabindex: String,
    readonly: Boolean,
    disableMask: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      menu: false,
      localDate: '',
      maskedDate: '',
      mask: process.env.VUE_APP_GERMAN_DATE_MASK,
      isLoading: false,
      minDate: null,
      maxDate: null
    }
  },
  methods: {
    ruleRequired,
    ruleValidDate,
    ruleFutureDate,
    rulePastDate,

    onClearClicked() {
      this.localDate = null;
    },

    resetData() {
      this.reset();
      this.localDate = '';
      this.maskedDate = '';
      this.minDate = null;
      this.maxDate = null;
    },

    setMaxDate(maxDate, exactDate = false) {
      if (typeof maxDate !== 'string' || maxDate.length === 0) return;

      let dateTemplate = process.env.VUE_APP_DATE_FORMAT_DTO;
      if(maxDate.includes('-')) {
        dateTemplate = 'yyyy-MM-dd';
      }

      let date = DateUtils.parseStringToDate(maxDate, dateTemplate);
      if (!exactDate) {
        date.setDate(date.getDate() + 1);
      }

      this.maxDate = date.toISOString().substr(0, 10);
    },

    resetMaxDate() {
      this.maxDate = null;
    },

    setMinDate(minDate, exactDate = false) {
      if (typeof minDate !== 'string' || minDate.length === 0) return;

      let dateTemplate = process.env.VUE_APP_DATE_FORMAT_DTO;
      if(minDate.includes('-')) {
        dateTemplate = 'yyyy-MM-dd';
      }

      let date = DateUtils.parseStringToDate(minDate, dateTemplate);
      if (!exactDate) {
        date.setDate(date.getDate() + 1);
      }

      this.minDate = date.toISOString().substr(0, 10);
    },

    resetMinDate() {
      this.minDate = null;
    },

    changedMaskDate() {
      // INFO: if below is uncommented, the menu can't be opened if this.maskedDate is set
      // with a default value via $refs.data.maskedDate. It takes three clicks to work
      // this.$refs.menu.save(this.maskedDate);
      this.$emit('update', this.maskedDate)
    },

    changeDate() {
      this.$refs.menu.save(this.localDate);
      this.$emit('update', this.localDate)
      this.menu = false;
    },

    parseDTO(date) {
      this.localDate = DateUtils.parseDateForVuetify(date);
      return DateUtils.parseDateTimeForDisplay(this.localDate, process.env.VUE_APP_DATE_FORMAT_DTO);
    },

    reset() {
      Object.assign(this.$data, this.$options.data());
    }
  },
  computed: {
    computedRuleCheck() {

      if (this.onlyPast && !this.onlyFuture) {
        return rulePastDate(this.$t('utils:rules.date-past-allowed'));
      } else if (!this.onlyPast && this.onlyFuture) {
        if (this.localDate === null || this.localDate === "" ) return true;
        return ruleFutureDate(this.$t('utils:rules.date-future-allowed'));
      } else if (this.maxDate) {
        return ruleMaxDate(this.$t('utils:rules.date-max-achieved', {date: DateUtils.parseDateTimeForDisplay(this.maxDate, process.env.VUE_APP_DATE_FORMAT)}), this.maxDate);
      } else if (this.minDate) {
        return ruleMinDate(this.$t('utils:rules.date-min-achieved', {date: DateUtils.parseDateTimeForDisplay(this.minDate, process.env.VUE_APP_DATE_FORMAT)}), this.minDate);
      } else {
        return true;
      }
    },
    computedDateFormatted: {
      get() {
        if (this.localDate) {
          return this.localDate ? DateUtils.parseDateTimeForDisplay(this.localDate, process.env.VUE_APP_DATE_FORMAT) : '';
        }
        return null
      },

      set(value) {
        if (value) {
          value = DateUtils.parseDateTimeForDisplay(value, process.env.VUE_APP_DATE_FORMAT_DTO);
        }
        this.maskedDate = value;
        this.$emit('update', value)
      }
    },
    today() {
      return new Date().toISOString().substr(0, 10)
    },
    yesterday() {
      var date = new Date()
      date.setDate(date.getDate() - 1);

      return date.toISOString().substr(0, 10);
    },
    getRules() {
      if (this.isRequired) {
        return [ruleRequired(this.$t('utils:rules.required')), ruleValidDate(this.$t('utils:rules.valid-date')), this.computedRuleCheck]
      } else {
        return [ruleValidDate(this.$t('utils:rules.valid-date')), this.computedRuleCheck]
      }
    }
  },
  watch: {
    localDate() {
      this.computedDateFormatted = this.localDate
    },
    maskedDate() {
      this.$emit('update', this.maskedDate)
    },
    menu() {
      if(this.menu) {
        if(this.maskedDate) {
          this.parseDTO(this.maskedDate);
        }
      }
    }
  },
  created() {
    let initDate = !this.initDate;
    if (!initDate) {
      this.parseDTO(this.initDate);
    }
  }
}
</script>
